import { useEffect, useRef } from "react";
import { levelOf } from "./RiskTable";

const scoreColor = (score) =>
  score > 15 ? "var(--danger)" : score > 8 ? "var(--warn)" : "var(--ok)";

const cellClass = (p, i) => {
  const s = p * i;
  if (s > 15) return "mc-c";
  if (s > 8)  return "mc-h";
  if (s > 4)  return "mc-m";
  return "mc-l";
};

export default function PresentationMode({ risks, onClose }) {
  const chartRef = useRef(null);
  const instRef  = useRef(null);

  useEffect(() => {
    const build = () => {
      if (!window.Chart || !chartRef.current || !risks.length) return;
      if (instRef.current) { instRef.current.destroy(); instRef.current = null; }
      const sorted = [...risks].sort((a, b) => b.score - a.score);
      instRef.current = new window.Chart(chartRef.current, {
        type: "bar",
        data: {
          labels: sorted.map((r) => r.name.slice(0, 14)),
          datasets: [{
            data: sorted.map((r) => r.score),
            backgroundColor: sorted.map((r) => r.score > 15 ? "#f4564e" : r.score > 8 ? "#f5a623" : "#42c97a"),
            borderRadius: 4, borderSkipped: false,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1a1b28", padding: 8, cornerRadius: 6 } },
          scales: {
            x: { grid: { display: false }, ticks: { color: "#5a5a78", font: { size: 9 } } },
            y: { grid: { color: "rgba(255,255,255,0.04)" }, ticks: { color: "#5a5a78", font: { size: 9 } }, beginAtZero: true },
          },
        },
      });
    };

    if (window.Chart) build();
    else {
      const t = setInterval(() => { if (window.Chart) { clearInterval(t); build(); } }, 80);
      return () => clearInterval(t);
    }
    return () => { if (instRef.current) { instRef.current.destroy(); instRef.current = null; } };
  }, [risks]);

  // Stats
  const critical = risks.filter((r) => r.score > 15 || r.rpn > 500).length;
  const high     = risks.filter((r) => (r.score > 8 && r.score <= 15) || (r.rpn > 200 && r.rpn <= 500)).length;
  const avgScore = risks.length ? Math.round(risks.reduce((s, r) => s + r.score, 0) / risks.length) : 0;
  const avgRPN   = risks.length ? Math.round(risks.reduce((s, r) => s + r.rpn,   0) / risks.length) : 0;

  // Matrix cells
  const ROWS = [5, 4, 3, 2, 1];
  const COLS = [1, 2, 3, 4, 5];

  // Top 8 risks for table
  const topRisks = [...risks].sort((a, b) => b.score - a.score).slice(0, 8);

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="pres-overlay">
      {/* Top bar */}
      <div className="pres-topbar">
        <div className="pres-logo">Risk Intelligence Report</div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div className="pres-date">{today}</div>
          <button className="btn" onClick={onClose}>✕ Exit</button>
        </div>
      </div>

      {/* Body */}
      <div className="pres-body">
        {/* Left column */}
        <div className="pres-col">
          {/* Summary stats */}
          <div className="pres-section" style={{ flex: "0 0 auto" }}>
            <div className="pres-section-label">Summary</div>
            <div className="pres-stats-grid">
              {[
                { label: "Total Risks", value: risks.length, color: "var(--text)" },
                { label: "Critical",    value: critical,      color: "var(--danger)" },
                { label: "High",        value: high,          color: "var(--warn)" },
                { label: "Avg Score",   value: avgScore,      color: "var(--accent)" },
                { label: "Avg RPN",     value: avgRPN,        color: "var(--text)" },
              ].map(({ label, value, color }) => (
                <div className="pres-stat" key={label}>
                  <div className="pres-stat-val" style={{ color }}>{value}</div>
                  <div className="pres-stat-label">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Matrix */}
          <div className="pres-section" style={{ flex: 1 }}>
            <div className="pres-section-label">Risk Matrix</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "24px repeat(5, 1fr)",
                gap: 3,
              }}
            >
              <div />
              {COLS.map((c) => (
                <div key={c} style={{ textAlign: "center", fontSize: 9, fontFamily: "var(--fm)", color: "var(--muted)" }}>{c}</div>
              ))}
              {ROWS.map((row) => (
                <>
                  <div key={`h-${row}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontFamily: "var(--fm)", color: "var(--muted)" }}>{row}</div>
                  {COLS.map((col) => {
                    const here = risks.filter((r) => r.prob === row && r.imp === col);
                    return (
                      <div
                        key={`${row}-${col}`}
                        className={`m-cell ${cellClass(row, col)}`}
                        style={{ minHeight: 36 }}
                      >
                        {here.map((r) => (
                          <span className="m-chip" key={r.id} style={{ fontSize: 8 }} title={r.name}>
                            {r.name.slice(0, 3)}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="pres-col">
          {/* Risk table */}
          <div className="pres-section" style={{ flex: 2, overflow: "auto" }}>
            <div className="pres-section-label">Risk Register — Top Risks</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Risk", "Category", "P", "I", "Score", "RPN", "Level"].map((h) => (
                    <th key={h} style={{
                      textAlign: "left", padding: "6px 8px",
                      fontSize: 9, fontFamily: "var(--fm)", color: "var(--muted)",
                      textTransform: "uppercase", letterSpacing: ".06em",
                      borderBottom: "1px solid var(--border)",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topRisks.map((r) => {
                  const lv = levelOf(r);
                  return (
                    <tr key={r.id}>
                      <td style={{ padding: "7px 8px", fontWeight: 500 }}>{r.name}</td>
                      <td style={{ padding: "7px 8px" }}><span className="cat-pill" style={{ fontSize: 9 }}>{r.cat}</span></td>
                      <td style={{ padding: "7px 8px", fontFamily: "var(--fm)" }}>{r.prob}</td>
                      <td style={{ padding: "7px 8px", fontFamily: "var(--fm)" }}>{r.imp}</td>
                      <td style={{ padding: "7px 8px", fontFamily: "var(--fm)", fontWeight: 600, color: scoreColor(r.score) }}>{r.score}</td>
                      <td style={{ padding: "7px 8px", fontFamily: "var(--fm)" }}>{r.rpn}</td>
                      <td style={{ padding: "7px 8px" }}><span className={`badge ${lv.cls}`}>{lv.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Score chart */}
          <div className="pres-section" style={{ flex: 1 }}>
            <div className="pres-section-label">Score Distribution</div>
            <div style={{ position: "relative", height: 160 }}>
              <canvas ref={chartRef} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="pres-footer">
        <div className="pres-footer-text">CONFIDENTIAL — RISK INTELLIGENCE REPORT</div>
        <div className="pres-footer-text">{risks.length} RISKS IDENTIFIED</div>
      </div>
    </div>
  );
}